import { useEffect } from "react";
import { createFileRoute, Link, Navigate, redirect, useRouter } from "@tanstack/react-router";
import { type QueryClient } from "@tanstack/react-query";
import { useQuery } from "../../lib/server-query";
import { Code, ConnectError } from "@connectrpc/connect";
import { Icon } from "@sx/ui";
import { Button, EmptyState, Page, PageHeader } from "@sx/ui";

import { Region } from "../../components/Region";
import type { ProjectAddress } from "../../lib/nav";
import { cachedProjectAt } from "../../lib/prefetch";
import { autonomyQueryKeys } from "../../lib/query-keys";
import { loadedSession } from "../../lib/session";
import { ConnectCodingAgent } from "../../components/ConnectCodingAgent";
import { useProject, useProjectId } from "../../shell/project";
import {
  projectManagerOf,
  teamRosterQuery,
  type TeamRoster,
} from "../../workspaces/agents/api";
import { AgentPageLoading } from "../../workspaces/agents/AgentPageLoading";
import {
  SetUpProjectTeam,
  useMaySetUpTeam,
} from "../../workspaces/agents/SetUpProjectTeam";

export const Route = createFileRoute("/_console/p/$org/$project/loop/manager")({
  // A roster already here names the main agent: go straight to the project's home, where its
  // conversation is. Otherwise the agent page's frame draws at once, and the roster and the main agent's conversation are
  // started together behind it. That frame is all this page needs to draw, so its code
  // never waits on the agent page's own (the conversation, its tabs, their renderers).
  loader: ({ context, params }) => {
    const address = { org: params.org, project: params.project };
    const manager = cachedProjectManager(context.queryClient, address);
    if (manager !== undefined) {
      throw redirect({ to: "/p/$org/$project", params: address, replace: true });
    }
    void import("../../workspaces/agents/prefetch").then(({ prefetchAgentPage }) =>
      prefetchAgentPage(context.queryClient, address, undefined, undefined),
    );
  },
  component: AssistantRoute,
});

/** The main agent the cache already names for this address, read without a request (or the
 *  agents' client, which this half of the route must not load). */
function cachedProjectManager(
  queryClient: QueryClient,
  address: ProjectAddress,
): string | undefined {
  const project = cachedProjectAt(
    queryClient,
    loadedSession(queryClient)?.principal.id,
    address,
  );
  if (project === undefined) return undefined;
  return queryClient
    .getQueryData<TeamRoster>(autonomyQueryKeys.project(project.id, "agent-teams"))
    ?.agents.find((agent) => agent.projectManager)?.agentId;
}

/** The project's main agent leads its agents — the Project Manager, or in a platform project
 *  the Operations manager: this address opens its conversation. It only reads; a project
 *  without one offers to set its team up. */
function AssistantRoute() {
  const projectId = useProjectId();
  const address = useProject().address;
  const roster = useQuery(teamRosterQuery(projectId));
  const maySetUp = useMaySetUpTeam();
  // The project's home is where this address goes: its code arrives while the roster does,
  // so going there draws at once.
  const router = useRouter();
  useEffect(() => {
    void router.loadRouteChunk(
      router.routesById["/_console/p/$org/$project/"],
    );
  }, [router]);
  if (!address) return null;
  if (
    roster.error instanceof ConnectError &&
    roster.error.code === Code.PermissionDenied
  ) {
    return (
      <Page layout="page">
        <PageHeader
          title="Project chat"
          actions={<ConnectCodingAgent projectId={projectId} />}
        />
        <EmptyState
          compact
          title="Project chat is not available with your current access."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/p/$org/$project/tasks" params={address}>
                <Icon name="design" /> Tasks
              </Link>
            </Button>
          }
        />
      </Page>
    );
  }
  return (
    <Region
      query={roster}
      title={roster.data?.mainAgentTitle || "Project Manager"}
      // The main agent's page as it will stand, so arriving there moves nothing.
      placeholder={<AgentPageLoading tab={undefined} />}
      still
    >
      {(answer) => {
        const manager = projectManagerOf(answer);
        const title = answer.mainAgentTitle || "Project Manager";
        return manager ? (
          <>
            {/* Held until the project's home replaces it, so nothing blanks between. */}
            <AgentPageLoading tab={undefined} />
            <Navigate to="/p/$org/$project" params={address} replace />
          </>
        ) : (
          <Page layout="page">
            <PageHeader title={title} />
            {answer.teamComplete ? (
              // A main agent someone retired or deleted is not set up again by viewing this page.
              <EmptyState compact title={`This project's ${title} was retired or deleted.`} />
            ) : maySetUp ? (
              <EmptyState
                compact
                title={`This project has no ${title} yet.`}
                action={<SetUpProjectTeam projectId={projectId} title={title} />}
              />
            ) : (
              <EmptyState
                compact
                title={`This project has no ${title} yet.`}
                hint="Someone who can add agents to this project can set it up."
              />
            )}
          </Page>
        );
      }}
    </Region>
  );
}
